import { useScroll, useTransform, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { neurons } from '../../data/neurons';
import NeuralNet from './NeuralNet';
import './blueprint.css';

const employment = neurons.filter(n => n.type === 'employment');
const projects   = neurons.filter(n => n.type === 'project');
const skills     = neurons.filter(n => n.type === 'skill');
const education  = neurons.filter(n => n.type === 'education');
const personal   = neurons.filter(n => n.type === 'personal');

const SKILL_GROUPS = [
  {
    label: 'SOFTWARE',
    ids: ['skill-react', 'skill-python', 'skill-aws', 'skill-matlab', 'skill-cplusplus'],
  },
  {
    label: 'BIOMEDICAL',
    ids: ['skill-gmp', 'skill-hplc', 'skill-solidworks', 'skill-analytical-chem', 'skill-process-eng'],
  },
  {
    label: 'DOMAIN',
    ids: [
      'skill-regulatory',
      'skill-tech-writing',
      'skill-cross-functional',
      'skill-startup',
      'skill-behavioral-neuroscience',
    ],
  },
];

const NAV_LINKS = [
  ['#experience', 'EXPERIENCE'],
  ['#projects',   'PROJECTS'],
  ['#skills',     'SKILLS'],
  ['#profile',    'PROFILE'],
];

function SectionHeader({ number, title }) {
  return (
    <motion.div
      className="bp-section-header"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6 }}
    >
      <span className="bp-section-number">{number}</span>
      <div className="bp-section-rule" />
      <h2 className="bp-section-title">{title}</h2>
      <div className="bp-section-rule-long" />
    </motion.div>
  );
}

export default function BlueprintPortfolio() {
  // Track full-page scroll — hand assembles as you scroll through all content
  const { scrollYProgress } = useScroll();

  // Background hand: barely visible during content, revealed in the finale
  const handBgOpacity = useTransform(
    scrollYProgress,
    [0, 0.72, 0.88, 1.0],
    [0.07, 0.09, 0.38, 0.54]
  );

  return (
    <div className="bp-root">
      <div className="bp-grid-bg" />

      {/* Fixed background hand — always centered, assembles with page scroll */}
      <motion.div className="bp-hand-bg" style={{ opacity: handBgOpacity }}>
        <NeuralNet scrollProgress={scrollYProgress} />
      </motion.div>

      {/* ── NAV ── */}
      <nav className="bp-nav">
        <Link to="/" className="bp-nav-logo">AS</Link>
        <ul className="bp-nav-links">
          {NAV_LINKS.map(([href, label]) => (
            <li key={href}><a href={href}>{label}</a></li>
          ))}
        </ul>
      </nav>

      {/* ── HERO ── */}
      <section className="bp-hero">
        <div className="bp-hero-content">
          <p className="bp-label">PORTFOLIO — 2026</p>
          <h1 className="bp-name">Adam<br />Shaker</h1>
          <div className="bp-divider" />
          <div className="bp-roles">
            <span className="bp-role">BIOMEDICAL ENGINEER</span>
            <span className="bp-role">SOFTWARE ENGINEER</span>
          </div>
          <p className="bp-tagline">
            Building at the intersection of biology and code. From GMP
            cleanrooms to cloud infrastructure — precision-engineered,
            end to end.
          </p>
          <div className="bp-hero-tags">
            {['React', 'AWS', 'TypeScript', 'GMP', 'Biotech', 'Python'].map(tag => (
              <span key={tag} className="bp-hero-tag">{tag}</span>
            ))}
          </div>
        </div>
        <div className="bp-scroll-hint">
          <div className="bp-scroll-line" />
          <span>SCROLL TO ASSEMBLE</span>
        </div>
      </section>

      {/* ── CONTENT ── */}
      <main className="bp-main">

        {/* EXPERIENCE */}
        <section id="experience" className="bp-section">
          <SectionHeader number="01" title="Experience" />
          <div className="bp-timeline">
            {employment.map((job, i) => (
              <motion.div
                key={job.id}
                className="bp-timeline-item"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
              >
                <div className="bp-timeline-date">{job.dates}</div>
                <div className="bp-timeline-connector">
                  <div className="bp-timeline-dot" />
                </div>
                <Link to={`/node/${job.id}`} className="bp-card-link">
                  <div className="bp-timeline-card">
                    <span className="bp-card-number">{String(i + 1).padStart(2, '0')}</span>
                    <h3 className="bp-company">{job.label}</h3>
                    <p className="bp-role-title">{job.role}</p>
                    <p className="bp-card-desc">{job.description}</p>
                    <div className="bp-tags">
                      {job.tags.map(tag => (
                        <span key={tag} className="bp-tag">{tag}</span>
                      ))}
                    </div>
                    <span className="bp-card-view-hint">VIEW →</span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>

        {/* PROJECTS */}
        <section id="projects" className="bp-section">
          <SectionHeader number="02" title="Projects" />
          <div className="bp-projects-grid">
            {projects.map((proj, i) => (
              <Link key={proj.id} to={`/node/${proj.id}`} className="bp-card-link">
                <motion.div
                  className="bp-project-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <p className="bp-proj-num">PROJECT / {String(i + 1).padStart(2, '0')}</p>
                  <h3 className="bp-proj-title">{proj.label}</h3>
                  <p className="bp-proj-desc">{proj.description}</p>
                  <div className="bp-tags">
                    {proj.tags.map(tag => (
                      <span key={tag} className="bp-tag">{tag}</span>
                    ))}
                  </div>
                  <span className="bp-card-view-hint">VIEW →</span>
                </motion.div>
              </Link>
            ))}
          </div>
        </section>

        {/* SKILLS */}
        <section id="skills" className="bp-section">
          <SectionHeader number="03" title="Skills" />
          <div className="bp-skills-layout">
            {SKILL_GROUPS.map(group => {
              const groupSkills = group.ids
                .map(id => skills.find(s => s.id === id))
                .filter(Boolean);
              return (
                <div key={group.label}>
                  <p className="bp-skill-group-title">{group.label}</p>
                  <div className="bp-skill-rows">
                    {groupSkills.map((skill, i) => (
                      <motion.div
                        key={skill.id}
                        className="bp-skill-row"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, margin: '-40px' }}
                        transition={{ duration: 0.4, delay: i * 0.06 }}
                      >
                        <div className="bp-skill-meta">
                          <span className="bp-skill-name">{skill.label}</span>
                          <span className="bp-skill-rating">{skill.importance}/10</span>
                        </div>
                        <div className="bp-skill-bar">
                          <motion.div
                            className="bp-skill-fill"
                            style={{ originX: 0 }}
                            initial={{ scaleX: 0 }}
                            whileInView={{ scaleX: skill.importance / 10 }}
                            viewport={{ once: true, margin: '-40px' }}
                            transition={{ duration: 0.8, delay: i * 0.06 + 0.2, ease: 'easeOut' }}
                          />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* PROFILE */}
        <section id="profile" className="bp-section">
          <SectionHeader number="04" title="Profile" />
          <div className="bp-profile-layout">
            <div>
              <p className="bp-skill-group-title">EDUCATION</p>
              <div className="bp-edu-items">
                {education.map((edu, i) => (
                  <motion.div
                    key={edu.id}
                    className="bp-edu-item"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.5, delay: i * 0.1 }}
                  >
                    <h4 className="bp-edu-school">{edu.label}</h4>
                    <p className="bp-edu-degree">{edu.role} — {edu.dates}</p>
                    <p className="bp-edu-desc">{edu.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
            <div>
              <p className="bp-personal-label">INTERESTS</p>
              <div className="bp-personal-grid">
                {personal.map((item, i) => (
                  <motion.div
                    key={item.id}
                    className="bp-personal-item"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true, margin: '-40px' }}
                    transition={{ duration: 0.4, delay: i * 0.07 }}
                  >
                    {item.label}
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

      </main>

      {/* ── FINALE — open space to appreciate the fully assembled hand ── */}
      <section className="bp-finale">
        <motion.div
          className="bp-finale-content"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 1.2 }}
        >
          <p className="bp-label">ASSEMBLY COMPLETE</p>
          <div className="bp-divider bp-finale-divider" />
          <p className="bp-finale-contact">adam.r.shaker@gmail.com</p>
          <p className="bp-finale-sub">ADAM SHAKER — 2026</p>
        </motion.div>
      </section>
    </div>
  );
}
